import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styles from './ProjectsGrid.module.scss';
import { BsGrid, BsListUl } from 'react-icons/bs';
import { getSiteUi } from '../../../lib/site-ui';

export default function ProjectsGrid({ categories = [], projects = [], languageCode = 'no' }) {
  const ui = getSiteUi(languageCode);
  const isEn = languageCode === 'en';
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [view, setView] = useState('grid');
  const [visibleCount, setVisibleCount] = useState(6);

  const ITEMS_PER_PAGE = 6;

  const categoriesWithCounts = (categories || [])
    .map((cat) => {
      const count = projects.filter((p) =>
        p.prosjektKategorier?.nodes?.some((c) => c.slug === cat.slug)
      ).length;
      return { ...cat, count };
    })
    .filter((cat) => cat.count > 0);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setVisibleCount(ITEMS_PER_PAGE);
  };

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + ITEMS_PER_PAGE);
  };

  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(project => project.prosjektKategorier?.nodes?.some(cat => cat.slug === selectedCategory));

  const visibleProjects = filteredProjects.slice(0, visibleCount);
  const hasMoreProjects = filteredProjects.length > visibleCount;

  // Helper to get optimized image source based on view type
  const getOptimizedImageSrc = (featuredImage, isListView = false) => {
    if (!featuredImage?.node) return null;
    
    const sizes = featuredImage.node.mediaDetails?.sizes || [];
    
    if (isListView) {
      // For list view, use larger images
      const large = sizes.find(size => size.name === 'large');
      const mediumLarge = sizes.find(size => size.name === 'medium_large');
      return large?.sourceUrl || mediumLarge?.sourceUrl || 
             featuredImage.node.mediaItemUrl || featuredImage.node.sourceUrl;
    } else {
      // For grid view, use medium-sized images
      const mediumLarge = sizes.find(size => size.name === 'medium_large');
      const medium = sizes.find(size => size.name === 'medium');
      return mediumLarge?.sourceUrl || medium?.sourceUrl || 
             featuredImage.node.mediaItemUrl || featuredImage.node.sourceUrl;
    }
  };

  if (!projects?.length) return null;

  return (
    <section className={styles.projectsSection}>
      <div className={styles.container}>
        <div className={styles.header}>
        <div className={styles.categoriesWrapper}>
          {/* Desktop Categories */}
          <div className={styles.categories}>
            <button
              className={`${styles.categoryButton} ${selectedCategory === 'all' ? styles.active : ''}`}
              onClick={() => handleCategoryChange('all')}
            >
              {isEn ? 'All projects' : 'Alle prosjekter'} ({projects.length})
            </button>
            {categoriesWithCounts.map(category => (
              <button
                key={category.id}
                className={`${styles.categoryButton} ${selectedCategory === category.slug ? styles.active : ''}`}
                onClick={() => handleCategoryChange(category.slug)}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>

          {/* Mobile Dropdown */}
          <select
            className={styles.mobileSelect}
            value={selectedCategory}
            onChange={(e) => handleCategoryChange(e.target.value)}
          >
            <option value="all">{isEn ? 'All projects' : 'Alle prosjekter'} ({projects.length})</option>
            {categoriesWithCounts.map(category => (
              <option key={category.id} value={category.slug}>
                {category.name} ({category.count})
              </option>
            ))}
          </select>
        </div>
        <div className={styles.viewSwitcher}>
          <button 
            className={`${styles.viewButton} ${view === 'list' ? styles.active : ''}`}
            onClick={() => setView('list')}
            aria-label="List view"
          >
            <BsListUl />
          </button>
          <button 
            className={`${styles.viewButton} ${view === 'grid' ? styles.active : ''}`}
            onClick={() => setView('grid')}
            aria-label="Grid view"
          >
            <BsGrid />
          </button>
        </div>
      </div>

        <div className={`${styles.projects} ${styles[view]}`}>
          {visibleProjects.map((project, index) => {
            // Keep only first row as high-priority candidates
            const isPriority = index < 3;
            
            const gridImageSrc = getOptimizedImageSrc(project.featuredImage, false);
            const listImageSrc = getOptimizedImageSrc(project.featuredImage, true);
            
                         return (
               <Link 
                 key={project.id} 
                 href={project.uri || `${isEn ? '/en' : ''}/prosjekt/${project.slug}` || '#'}
                 className={styles.projectCard}
               >
                {view === 'grid' ? (
                  <>
                    <div className={styles.imageWrapper}>
                      {gridImageSrc && (
                        <Image
                          src={gridImageSrc}
                          alt={project.featuredImage?.node?.altText || project.title}
                          className={styles.image}
                          fill
                          sizes="(max-width: 480px) 95vw, (max-width: 768px) 48vw, (max-width: 1024px) 32vw, 400px"
                          priority={isPriority}
                          quality={75}
                        />
                      )}
                    </div>
                    <div className={styles.content}>
                      <h3>{project.title}</h3>
                      {project.excerpt && (
                        <div 
                          className={styles.excerpt}
                          dangerouslySetInnerHTML={{ __html: project.excerpt }}
                        />
                      )}
                      <div className={styles.readMoreLink}>
                        {ui.readMore}
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z" fill="currentColor"/>
                        </svg>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.imageWrapper}>
                      {listImageSrc && (
                        <Image
                          src={listImageSrc}
                          alt={project.featuredImage?.node?.altText || project.title}
                          className={styles.image}
                          fill
                          sizes="(max-width: 480px) 100vw, (max-width: 768px) 90vw, (max-width: 1024px) 80vw, 1200px"
                          priority={isPriority}
                          quality={75}
                        />
                      )}
                    </div>
                    <div className={styles.overlay}>
                      <span className={styles.featured}>{isEn ? 'Featured project' : 'Utvalgt prosjekt'}</span>
                      <h2>{project.title}</h2>
                      {project.excerpt && (
                        <div 
                          className={styles.excerpt}
                          dangerouslySetInnerHTML={{ __html: project.excerpt }}
                        />
                      )}
                    </div>
                  </>
                )}
              </Link>
            );
          })}
        </div>

        {hasMoreProjects && (
          <div className={styles.loadMoreWrapper}>
            <button 
              className={styles.loadMoreButton}
              onClick={handleLoadMore}
            >
              {ui.showMore}
            </button>
          </div>
        )}
      </div>
    </section>
  );
} 